import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Plus, Search, Edit2, Trash2, Megaphone, Calendar, ChevronRight } from "lucide-react";
import { CONTENT } from "@/constants/testIds";

import { API } from "../lib/api";
const ax = () => axios.create({ withCredentials: true });

function formatDate(dt) { try { return new Date(dt).toLocaleDateString("pt-BR"); } catch { return dt; } }

export default function CampaignsPage() {
  const { hasAnyRole } = useAuth();
  const canEdit = hasAnyRole(["autor_area", "administrador_total", "admin"]);
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title: "", content: "", category: "", start_date: "", end_date: "", tags: "" });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selected, setSelected] = useState(null);

  const load = () => ax().get(`${API}/campaigns`).then(r => setItems(r.data)).catch(console.error).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const active = items.filter(i => i.is_active);
  const inactive = items.filter(i => !i.is_active);
  const filtered = items.filter(i => i.title?.toLowerCase().includes(search.toLowerCase()));

  const openCreate = () => { setEditing(null); setForm({ title: "", content: "", category: "", start_date: "", end_date: "", tags: "" }); setShowForm(true); };
  const openEdit = (item) => { setEditing(item); setForm({ title: item.title, content: item.content, category: item.category || "", start_date: item.start_date || "", end_date: item.end_date || "", tags: (item.tags || []).join(", ") }); setShowForm(true); };

  const save = async () => {
    setSaving(true);
    const payload = { ...form, tags: form.tags ? form.tags.split(",").map(t => t.trim()) : [] };
    try {
      if (editing) await ax().put(`${API}/campaigns/${editing.item_id}`, payload);
      else await ax().post(`${API}/campaigns`, payload);
      setShowForm(false); load();
    } catch (e) { console.error(e); }
    setSaving(false);
  };

  const remove = async (id) => {
    if (!window.confirm("Excluir esta campanha?")) return;
    await ax().delete(`${API}/campaigns/${id}`);
    load();
  };

  const CampaignCard = ({ item }) => (
    <div key={item.item_id} data-testid={CONTENT.contentItem} className="bento-card p-4 cursor-pointer group" onClick={() => setSelected(selected?.item_id === item.item_id ? null : item)}>
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <h3 className="font-semibold text-slate-800 group-hover:text-[#2346ff] transition-colors">{item.title}</h3>
            <Badge className={`text-xs ${item.is_active ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"}`}>
              {item.is_active ? "Ativa" : "Encerrada"}
            </Badge>
            {item.category && <Badge variant="secondary" className="text-xs">{item.category}</Badge>}
          </div>
          <p className="text-sm text-slate-500 line-clamp-2">{item.content}</p>
          {(item.start_date || item.end_date) && (
            <div className="flex items-center gap-1 mt-2">
              <Calendar className="w-3 h-3 text-slate-400" />
              <span className="text-xs text-slate-400">{item.start_date} — {item.end_date}</span>
            </div>
          )}
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          <ChevronRight className={`w-4 h-4 text-slate-400 transition-transform ${selected?.item_id === item.item_id ? "rotate-90" : ""}`} />
          {canEdit && (
            <>
              <Button variant="ghost" size="icon" className="h-7 w-7 opacity-0 group-hover:opacity-100" onClick={e => { e.stopPropagation(); openEdit(item); }}><Edit2 className="w-3.5 h-3.5" /></Button>
              <Button variant="ghost" size="icon" className="h-7 w-7 opacity-0 group-hover:opacity-100 text-red-500" onClick={e => { e.stopPropagation(); remove(item.item_id); }}><Trash2 className="w-3.5 h-3.5" /></Button>
            </>
          )}
        </div>
      </div>
      {selected?.item_id === item.item_id && (
        <div className="mt-3 pt-3 border-t border-slate-100">
          <p className="text-sm text-slate-700 whitespace-pre-wrap">{item.content}</p>
        </div>
      )}
    </div>
  );

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2">
          <Megaphone className="w-5 h-5 text-purple-500" />
          <div>
            <h1 className="text-xl font-bold font-heading text-slate-900">Campanhas</h1>
            <p className="text-sm text-slate-500">{active.length} ativas · {inactive.length} encerradas</p>
          </div>
        </div>
        {canEdit && (
          <Button data-testid={CONTENT.createBtn} onClick={openCreate} style={{ backgroundColor: "#2346ff" }} className="text-white gap-2">
            <Plus className="w-4 h-4" /> Nova Campanha
          </Button>
        )}
      </div>

      <div className="relative mb-4">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input placeholder="Buscar campanhas..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9" />
      </div>

      {loading ? (
        <div className="space-y-3">{[...Array(3)].map((_, i) => <div key={i} className="h-24 bg-slate-100 rounded-xl animate-pulse" />)}</div>
      ) : (
        <div data-testid={CONTENT.contentList} className="space-y-3">
          {active.filter(i => i.title?.toLowerCase().includes(search.toLowerCase())).length > 0 && (
            <>
              <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wider">Campanhas Ativas</h2>
              {active.filter(i => i.title?.toLowerCase().includes(search.toLowerCase())).map(item => <CampaignCard key={item.item_id} item={item} />)}
            </>
          )}
          {inactive.filter(i => i.title?.toLowerCase().includes(search.toLowerCase())).length > 0 && (
            <>
              <h2 className="text-sm font-semibold text-slate-400 uppercase tracking-wider mt-4">Campanhas Encerradas</h2>
              {inactive.filter(i => i.title?.toLowerCase().includes(search.toLowerCase())).map(item => <CampaignCard key={item.item_id} item={item} />)}
            </>
          )}
          {filtered.length === 0 && (
            <div className="text-center py-12 text-slate-400">
              <Megaphone className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>Nenhuma campanha encontrada</p>
            </div>
          )}
        </div>
      )}

      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="font-heading">{editing ? "Editar Campanha" : "Nova Campanha"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Título *</Label><Input value={form.title} onChange={e => setForm(f => ({...f, title: e.target.value}))} /></div>
            <div><Label>Descrição *</Label><Textarea rows={4} value={form.content} onChange={e => setForm(f => ({...f, content: e.target.value}))} /></div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label>Categoria</Label><Input value={form.category} onChange={e => setForm(f => ({...f, category: e.target.value}))} placeholder="Ex: Vendas" /></div>
              <div><Label>Data Início</Label><Input type="date" value={form.start_date} onChange={e => setForm(f => ({...f, start_date: e.target.value}))} /></div>
              <div><Label>Data Fim</Label><Input type="date" value={form.end_date} onChange={e => setForm(f => ({...f, end_date: e.target.value}))} /></div>
            </div>
            <div><Label>Tags</Label><Input value={form.tags} onChange={e => setForm(f => ({...f, tags: e.target.value}))} placeholder="tag1, tag2" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowForm(false)}>Cancelar</Button>
            <Button onClick={save} disabled={saving || !form.title || !form.content} style={{ backgroundColor: "#2346ff" }} className="text-white">{saving ? "Salvando..." : "Salvar"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
